import type { Simplify } from 'type-fest';

import { renderRevision } from '../helpers/renderLocator';
import type { LocatorParts } from '../Locator';

import { ExceptionIgnoreScope, ExceptionPackageScope } from './request';
import type { Category } from './common';

export type IssueExceptionsFilter = {
  policyId?: number;
  projectId?: string;
  releaseGroupId?: number;
  category: Category.Licensing | Category.Vulnerability | Category.Quality,
}

export type IssueExceptionsPagination = {
  page: number;
  count: number;
}

/**
 * When User "Select's All", we remove all exceptions that match the filter criteria.
 */
export type IssueExceptionsRemoveCriteriaAll = {
  kind: 'all';
  filters: IssueExceptionsFilter;
};

/**
 * When User selects specific exceptions, we remove them by their id.
 */
export type IssueExceptionsRemoveCriteriaIdList = {
  kind: 'idList';
  value: number[];
};

/**
 * User can remove exceptions by either selecting all or by selecting specific exceptions.
 */
export type IssueExceptionsRemoveRequest = Simplify<{
  search?: string;
} & (IssueExceptionsRemoveCriteriaAll | IssueExceptionsRemoveCriteriaIdList)>;

export type IssueExceptionsRemoveResponse = {
  count: number;
}

export const scopeToBadgeText: Record<ExceptionIgnoreScope, Uppercase<string>> = {
  [ExceptionIgnoreScope.Project]: 'PROJECT',
  [ExceptionIgnoreScope.ProjectAndReleaseGroups]: 'PROJ + RG',
  [ExceptionIgnoreScope.ReleaseGroup]: 'RELEASE GROUP',
  [ExceptionIgnoreScope.Policy]: 'POLICY',
  [ExceptionIgnoreScope.Organization]: 'GLOBAL',
};

/**
 * Provides a human readable version, taking into account package scoping
 * @param packageScope {ExceptionPackageScope} Package scoping
 * @param revision {LocatorParts} Revision with version
 */
export const getDisplayVersion = (packageScope: ExceptionPackageScope, revision: LocatorParts | null) =>
  packageScope === ExceptionPackageScope.AllVersions || !revision?.revision ? 'All' : renderRevision(revision);

/**
 * Provides a human readable ignore scope
 * @param {ExceptionIgnoreScope} ignoreScope Ignore scope for the exception
 */
export const getIgnoreScopeLabel = (ignoreScope: ExceptionIgnoreScope) => scopeToBadgeText[ignoreScope] || 'UNKNOWN';
